import "./Locks.css";

import { ILockService, lockErrorKind, lockErrorMessage, lockService } from "../services/LockService";
import { LockPrincipalDTO, PageLockDTO, PageLockStateDTO } from "../models/LockDTO";
import { useCallback, useEffect, useState } from "react";

import { Button } from "../../../lib/components/buttons/Button";
import { DateTimeHelper } from "../../../lib/utils/DateTimeHelper";
import { IdentityUnavailableText } from "./LockStates";
import { Link } from "react-router-dom";
import { identityWebIamUrl } from "../utils/identityWeb";

interface ILockPanelProps {
    spaceId: string;
    pageId: string;
    /** Injected in tests; the shared facade client otherwise. */
    service?: ILockService;
    /** The caller's object id, to say "you" for the creator. */
    currentUserId?: string;
    /** After a lock or unlock went through (refresh the tree badges). */
    onChanged?: () => void;
    /** The page is no longer visible to the caller (they locked it and are not one of its readers yet). */
    onHidden?: () => void;
}

type Status = "loading" | "absent" | "unavailable" | "error" | "ready";
type Pending = "lock" | "unlock" | undefined;

export const LockNote =
    "Locking hides a page and its children from everyone except the lock's readers, space Owners and platform admins.";
const ReadersInIdentityWeb = "Readers are managed in Identity Web until Ark can manage them";
const CannotLock =
    "You cannot lock pages in this space: locking needs the Ark Contributor or Ark Owner role on the space.";
const CannotUnlock = "Only the lock's owners, space Owners and platform admins can unlock this page.";

function isLocked(state: PageLockStateDTO | undefined): state is PageLockDTO {
    return !!state && state.locked === true;
}

function principalLabel(p: LockPrincipalDTO): string {
    return p.displayName || p.email || p.id;
}

function PrincipalList(props: { label: string; principals: LockPrincipalDTO[] }) {
    return (
        <div className="lock-principals">
            <h4>{props.label}</h4>
            <ul aria-label={`Lock ${props.label.toLowerCase()}`}>
                {props.principals.length === 0 ? <li className="lock-principals-none">None</li> : null}
                {props.principals.map((p) => (
                    <li key={p.id} title={p.email || p.id}>
                        {principalLabel(p)}
                        {p.type && p.type !== "user" ? <span className="lock-principal-type"> ({p.type})</span> : null}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * The lock panel of a page (OrbitalPlan/18 D8). Only the page's readers ever see it: for anyone else the page is a 404. The SPA cannot
 * evaluate `ark.locks/create` or `ark.locks/manage` itself, so Lock and Unlock are offered and a 403 is explained instead.
 */
export function LockPanel(props: ILockPanelProps) {
    const service = props.service ?? lockService();
    const { spaceId, pageId, onChanged, onHidden } = props;

    const [status, setStatus] = useState<Status>("loading");
    const [state, setState] = useState<PageLockStateDTO>();
    const [pending, setPending] = useState<Pending>();
    const [reason, setReason] = useState("");
    const [isBusy, setIsBusy] = useState(false);
    const [actionError, setActionError] = useState<string>();

    const applyLoadError = useCallback((err: unknown) => {
        const kind = lockErrorKind(err);
        // 404: a server without lock routes (pre-1b) or a page gone meanwhile; either way there is nothing to show
        setStatus(kind === "not-found" ? "absent" : kind === "identity-unavailable" ? "unavailable" : "error");
    }, []);

    useEffect(() => {
        let alive = true;
        setStatus("loading");
        setPending(undefined);
        setActionError(undefined);
        service
            .getLock(spaceId, pageId)
            .then((s) => {
                if (!alive) return;
                setState(s);
                setStatus("ready");
            })
            .catch((err) => alive && applyLoadError(err));
        return () => {
            alive = false;
        };
    }, [service, spaceId, pageId, applyLoadError]);

    const actionFailed = useCallback((err: unknown, action: Exclude<Pending, undefined>) => {
        const kind = lockErrorKind(err);
        if (kind === "forbidden") setActionError(action === "lock" ? CannotLock : CannotUnlock);
        else if (kind === "identity-unavailable") setActionError(IdentityUnavailableText);
        else setActionError(lockErrorMessage(err));
        setIsBusy(false);
    }, []);

    /** Re-reads the lock after a change; a 404 now means the caller no longer reads the page. */
    const refreshAfterChange = useCallback(() => {
        return service.getLock(spaceId, pageId).then(
            (s) => {
                setState(s);
                setStatus("ready");
                setPending(undefined);
                setReason("");
                setIsBusy(false);
                onChanged?.();
            },
            (err) => {
                setIsBusy(false);
                setPending(undefined);
                if (lockErrorKind(err) === "not-found") {
                    onChanged?.();
                    onHidden?.();
                } else applyLoadError(err);
            }
        );
    }, [service, spaceId, pageId, onChanged, onHidden, applyLoadError]);

    const confirmLock = useCallback(() => {
        setIsBusy(true);
        setActionError(undefined);
        service.lock(spaceId, pageId, reason).then(refreshAfterChange, (err) => actionFailed(err, "lock"));
    }, [service, spaceId, pageId, reason, refreshAfterChange, actionFailed]);

    const confirmUnlock = useCallback(() => {
        setIsBusy(true);
        setActionError(undefined);
        service.unlock(spaceId, pageId).then(refreshAfterChange, (err) =>
            // 404: the page carries no lock of its own any more (someone else unlocked it): show the current state
            lockErrorKind(err) === "not-found" ? refreshAfterChange() : actionFailed(err, "unlock")
        );
    }, [service, spaceId, pageId, refreshAfterChange, actionFailed]);

    const cancel = useCallback(() => {
        setPending(undefined);
        setActionError(undefined);
    }, []);

    if (status === "loading" || status === "absent") return null;

    if (status === "unavailable") {
        return (
            <section className="lock-panel" aria-label="Page lock">
                <div className="lock-panel-unavailable">{IdentityUnavailableText}</div>
            </section>
        );
    }

    if (status === "error") {
        return (
            <section className="lock-panel" aria-label="Page lock">
                <div className="lock-panel-error">The lock details of this page could not be loaded.</div>
            </section>
        );
    }

    const lock = isLocked(state) ? state : undefined;
    const inherited = !!lock?.inherited;
    const manageUrl = lock ? lock.manageUrl || identityWebIamUrl(lock.scope) : undefined;
    const people = lock ? [...lock.owners, ...lock.readers] : [];
    const creator = lock
        ? props.currentUserId && lock.createdBy === props.currentUserId
            ? "you"
            : (() => {
                  const known = people.find((p) => p.id === lock.createdBy);
                  return known ? principalLabel(known) : lock.createdBy;
              })()
        : undefined;

    return (
        <section className={`lock-panel${lock ? " locked" : ""}`} aria-label="Page lock">
            {lock ? (
                <>
                    <div className="lock-panel-title">
                        <span className={`lock-badge${inherited ? " inherited" : ""}`} aria-hidden="true">{"\u{1F512}"}</span>
                        <span>Locked</span>
                    </div>
                    {inherited ? (
                        <div className="lock-panel-inherited">
                            This page is under the lock on{" "}
                            <Link to={`/spaces/${spaceId}/pages/${lock.pageId}`}>{lock.title}</Link>; it can be unlocked there.
                        </div>
                    ) : null}
                    <dl className="lock-panel-facts">
                        <dt>Locked by</dt>
                        <dd>{creator}</dd>
                        <dt>Locked on</dt>
                        <dd>{DateTimeHelper.formatDateTime(lock.createdAt)}</dd>
                        {lock.reason ? (<><dt>Reason</dt><dd>{lock.reason}</dd></>) : null}
                    </dl>

                    {lock.owners.length === 0 && lock.readers.length === 0 ? (
                        <div className="lock-panel-readers-empty">{ReadersInIdentityWeb}</div>
                    ) : (
                        <div className="lock-panel-people">
                            <PrincipalList label="Owners" principals={lock.owners} />
                            <PrincipalList label="Readers" principals={lock.readers} />
                        </div>
                    )}

                    <div className="lock-panel-actions">
                        {manageUrl ? (
                            <a className="button secondary lock-panel-manage" href={manageUrl} target="_blank" rel="noopener noreferrer">
                                Manage readers
                            </a>
                        ) : null}
                        {!inherited && pending !== "unlock" ? (
                            <Button text="Unlock" type="secondary" onClick={() => setPending("unlock")} isDisabled={isBusy} />
                        ) : null}
                    </div>

                    {!inherited && pending === "unlock" ? (
                        <div className="lock-panel-confirm" role="group" aria-label="Confirm unlock">
                            <div>Unlock this page? Everyone who reads the space will see it and its children again.</div>
                            {actionError ? <div className="lock-panel-error">{actionError}</div> : null}
                            <div className="lock-panel-actions">
                                <Button text="Confirm unlock" type="primary" onClick={confirmUnlock} isDisabled={isBusy} />
                                <Button text="Cancel" type="secondary" onClick={cancel} isDisabled={isBusy} />
                            </div>
                        </div>
                    ) : null}
                </>
            ) : (
                <div className="lock-panel-unlocked">
                    <span>This page is not locked.</span>
                    {pending !== "lock" ? (
                        <Button text="Lock this page" type="secondary" onClick={() => setPending("lock")} isDisabled={isBusy} />
                    ) : null}
                </div>
            )}

            {pending === "lock" ? (
                <div className="lock-panel-dialog" role="dialog" aria-label="Lock this page">
                    <h4>Lock this page</h4>
                    <p>{LockNote}</p>
                    <p className="lock-panel-warning">
                        Until Ark manages readers itself, you are not added as a reader automatically: unless you are a space Owner or a
                        platform admin, the page disappears for you until an Identity admin adds you as Lock Reader or Lock Owner in
                        Identity Web.
                    </p>
                    <label htmlFor={`lock-reason-${pageId}`}>Reason (optional)</label>
                    <textarea id={`lock-reason-${pageId}`} value={reason} rows={3} maxLength={1000}
                        onChange={(ev) => setReason(ev.target.value)} />
                    {actionError ? <div className="lock-panel-error">{actionError}</div> : null}
                    <div className="lock-panel-actions">
                        <Button text="Lock" type="primary" onClick={confirmLock} isDisabled={isBusy} />
                        <Button text="Cancel" type="secondary" onClick={cancel} isDisabled={isBusy} />
                    </div>
                </div>
            ) : null}

            {pending !== "lock" ? <div className="lock-panel-note">{LockNote}</div> : null}
        </section>
    );
}
